import { clientFor, type PageQuery, type PageResult } from '@yc/api-client'

import { listQuery } from './query'

const api = clientFor('/nos/api')

/** 网点状态：active 参与仿真取数，disabled 只停用、保留历史引用 */
export type SiteStatus = 'active' | 'disabled'

/** 网点类型：与后端 internal/site/model.go 的 Kinds 同源两份 */
export type SiteKind = 'warehouse' | 'depot' | 'transfer' | 'locker'

/** 网点档案。字段与 services/nos/internal/site/model.go 的 Site 一一对应。 */
export interface Site {
  id: number
  tenant_id: number
  code: string
  name: string
  kind: SiteKind
  city: string
  address: string
  /** 经度，列宽 DECIMAL(10,6)，取值范围 -180 ~ 180 */
  lng: number
  /** 纬度，列宽 DECIMAL(10,6)，取值范围 -90 ~ 90 */
  lat: number
  /** 日处理能力（件/日），仿真算法的负载分母 */
  capacity: number
  status: SiteStatus
  created_at: string
  updated_at: string
}

/** 新建 / 编辑网点的入参：编码后端必填（空串 422），其余可留空 */
export interface SiteInput {
  code: string
  kind: SiteKind
  status: SiteStatus
  name?: string
  city?: string
  address?: string
  lng?: number
  lat?: number
  capacity?: number
}

export interface SiteQuery extends PageQuery {
  keyword?: string
  status?: string
  kind?: string
  city?: string
}

/** 表单态：空文本用 ''，没填的数值用 undefined */
export interface SiteForm {
  code: string
  name: string
  kind: SiteKind
  city: string
  address: string
  lng: number | undefined
  lat: number | undefined
  capacity: number | undefined
  status: SiteStatus
}

/** 网点类型字典，顺序与后端 site.Kinds 一致，即前端下拉的展示顺序 */
export const KINDS: SiteKind[] = ['warehouse', 'depot', 'transfer', 'locker']

/** 与后端 services/nos/internal/site/model.go 的 KindNames 同源两份 */
export const KIND_LABELS: Record<SiteKind, string> = {
  warehouse: '仓库',
  depot: '站点/堆场',
  transfer: '转运中心',
  locker: '自提柜',
}

/** 与后端 services/nos/internal/site/model.go 的 StatusNames 同源两份 */
export const STATUS_LABELS: Record<SiteStatus, string> = {
  active: '启用',
  disabled: '停用',
}

/** 状态 → 表格里的标签色 */
export const STATUS_TAG: Record<SiteStatus, 'info' | 'primary' | 'warning' | 'success' | 'danger'> = {
  active: 'success',
  disabled: 'info',
}

/** 前端校验：把后端 site/service.go 的 validate 抄一份，提交前先挡住明显的 422 */
export function siteFormError(form: SiteForm): string {
  const code = form.code.trim()
  if (!code) return '请输入网点编码'
  if ([...code].length > 32) return '网点编码不能超过 32 个字符'
  if ([...form.name.trim()].length > 64) return '网点名称不能超过 64 个字符'
  if ([...form.city.trim()].length > 64) return '城市不能超过 64 个字符'
  if ([...form.address.trim()].length > 255) return '地址不能超过 255 个字符'
  const lng = form.lng
  if (lng !== undefined && (lng < -180 || lng > 180)) return '经度必须在 -180 到 180 之间'
  const lat = form.lat
  if (lat !== undefined && (lat < -90 || lat > 90)) return '纬度必须在 -90 到 90 之间'
  if (form.capacity !== undefined && form.capacity < 0) return '日处理能力不能为负数'
  return ''
}

/** 表单 → 请求体：空字段不进 body（undefined 在 JSON.stringify 里会被丢掉） */
export function siteInput(form: SiteForm): SiteInput {
  const input: SiteInput = {
    code: form.code.trim(),
    kind: form.kind,
    status: form.status,
  }
  const name = form.name.trim()
  if (name) input.name = name
  const city = form.city.trim()
  if (city) input.city = city
  const address = form.address.trim()
  if (address) input.address = address
  // 经纬度与能力没填就不上送：后端零值是合法值（0,0 / 0 件），显式送 0 与不送等价，
  // 但不送能保持「留空」与「填 0」在请求体里的区别，也不必在表单里塞占位 0。
  if (form.lng !== undefined && !Number.isNaN(form.lng)) input.lng = Number(form.lng)
  if (form.lat !== undefined && !Number.isNaN(form.lat)) input.lat = Number(form.lat)
  if (form.capacity !== undefined && !Number.isNaN(form.capacity)) {
    input.capacity = Number(form.capacity)
  }
  return input
}

/** 列表行与详情都是 Site 本身 */
export type SiteRow = Site

export const listSites = (q: SiteQuery): Promise<PageResult<SiteRow>> =>
  api.get<PageResult<SiteRow>>('/sites', listQuery({ ...q }))

export const getSite = (id: number): Promise<SiteRow> => api.get<SiteRow>(`/sites/${id}`)

export const createSite = (input: SiteInput): Promise<SiteRow> => api.post<SiteRow>('/sites', input)

export const updateSite = (id: number, input: SiteInput): Promise<SiteRow> =>
  api.put<SiteRow>(`/sites/${id}`, input)

export const deleteSite = (id: number): Promise<void> => api.del<void>(`/sites/${id}`)

/** 下拉参照用的全量网点：后端 size 上限 200，与其他子系统的参照口径一致 */
export const listSiteOptions = (): Promise<SiteRow[]> =>
  listSites({ page: 1, size: 200 }).then((page) => page.list)
